import { blockColour, blockModel } from './pipeline';
import { forEachSchematicColumn, schematicShift, schematicSize, type SchematicInput } from './schematic';
import type { MapartPalette, Rgb } from './types';

/** Face numbering the preview shader decodes; it lives in the low three bits of each face's last value. */
export const PREVIEW_FACE = { top: 0, bottom: 1, north: 2, south: 3, west: 4, east: 5 } as const;

/** Blocks no map colour lists, the support block usually among them, render in stone grey. */
const UNLISTED_BLOCK: Rgb = [125, 125, 125];

const SIDES = [
  { deltaX: 0, deltaZ: -1, face: PREVIEW_FACE.north },
  { deltaX: 0, deltaZ: 1, face: PREVIEW_FACE.south },
  { deltaX: -1, deltaZ: 0, face: PREVIEW_FACE.west },
  { deltaX: 1, deltaZ: 0, face: PREVIEW_FACE.east },
];

export interface PreviewMesh {
  /** Four values per visible face: block x, y, z in schematic space, then `colourIndex * 8 + face`. */
  faces: Uint16Array;
  faceCount: number;
  /** RGBA per colour index, what the block shows until its textures load and wherever they are see-through. */
  colours: Uint8Array<ArrayBuffer>;
  /** Blockstate location per colour index, null for a block the palette does not list. */
  models: (string | null)[];
  width: number;
  height: number;
  length: number;
}

class FaceWriter {
  values = new Uint16Array(4096);
  count = 0;

  push(x: number, y: number, z: number, face: number, colour: number) {
    if ((this.count + 1) * 4 > this.values.length) {
      const grown = new Uint16Array(this.values.length * 2);
      grown.set(this.values);
      this.values = grown;
    }
    const at = this.count * 4;
    this.values[at] = x;
    this.values[at + 1] = y;
    this.values[at + 2] = z;
    this.values[at + 3] = colour * 8 + face;
    this.count++;
  }
}

/** One exported schematic, at its column and row in the grid of maps. */
export interface PreviewBuild {
  input: SchematicInput;
  column: number;
  row: number;
}

/** Air between maps that are built on their own, so the preview shows them as separate builds. */
const BUILD_GAP = 4;

/**
 * The exported schematics as block faces. Every column is one unbroken run, its top block over its
 * support, so a side face is hidden exactly where the neighbouring column's run covers that height.
 */
export function buildPreviewMesh(builds: PreviewBuild[], palette: MapartPalette): PreviewMesh {
  const placed = builds.map((build) => {
    const shift = schematicShift(build.input);
    const footprint = schematicSize(build.input, shift);
    return {
      input: build.input,
      shift,
      originX: build.column * (footprint.width + BUILD_GAP),
      originZ: build.row * (footprint.length + BUILD_GAP),
      footprint,
    };
  });
  const size = {
    width: Math.max(...placed.map((build) => build.originX + build.footprint.width)),
    height: Math.max(...placed.map((build) => build.footprint.height)),
    length: Math.max(...placed.map((build) => build.originZ + build.footprint.length)),
  };
  const footprint = size.width * size.length;
  const tops = new Int32Array(footprint).fill(-1);
  const bottoms = new Int32Array(footprint);
  const topColours = new Uint16Array(footprint);

  const colourByBlock = new Map<number, number>();
  const colourValues: number[] = [];
  const models: (string | null)[] = [];
  const colourOf = (block: { id: number; meta: number }) => {
    const key = block.id * 16 + block.meta;
    const known = colourByBlock.get(key);
    if (known !== undefined) return known;
    const rgb = blockColour(palette, block.id, block.meta) ?? UNLISTED_BLOCK;
    colourValues.push(rgb[0], rgb[1], rgb[2], 255);
    models.push(blockModel(palette, block.id, block.meta));
    colourByBlock.set(key, colourValues.length / 4 - 1);
    return colourValues.length / 4 - 1;
  };
  const supportColour = colourOf(builds[0].input.supportBlock);

  for (const build of placed) {
    forEachSchematicColumn(build.input, build.shift, (column) => {
      const cell = (build.originZ + column.z) * size.width + build.originX + column.x;
      tops[cell] = column.y;
      bottoms[cell] = column.y - column.supportDepth;
      topColours[cell] = colourOf(column.block);
    });
  }

  const writer = new FaceWriter();
  for (let blockZ = 0; blockZ < size.length; blockZ++) {
    for (let blockX = 0; blockX < size.width; blockX++) {
      const cell = blockZ * size.width + blockX;
      const top = tops[cell];
      if (top < 0) continue;
      const bottom = bottoms[cell];
      const colourAt = (blockY: number) => (blockY === top ? topColours[cell] : supportColour);

      writer.push(blockX, top, blockZ, PREVIEW_FACE.top, topColours[cell]);
      writer.push(blockX, bottom, blockZ, PREVIEW_FACE.bottom, colourAt(bottom));

      for (const side of SIDES) {
        const neighbourX = blockX + side.deltaX;
        const neighbourZ = blockZ + side.deltaZ;
        const inside = neighbourX >= 0 && neighbourX < size.width && neighbourZ >= 0 && neighbourZ < size.length;
        const neighbourCell = neighbourZ * size.width + neighbourX;
        const neighbourTop = inside ? tops[neighbourCell] : -1;
        if (neighbourTop < 0) {
          for (let blockY = bottom; blockY <= top; blockY++) writer.push(blockX, blockY, blockZ, side.face, colourAt(blockY));
          continue;
        }
        const neighbourBottom = bottoms[neighbourCell];
        for (let blockY = bottom; blockY <= Math.min(top, neighbourBottom - 1); blockY++) {
          writer.push(blockX, blockY, blockZ, side.face, colourAt(blockY));
        }
        for (let blockY = Math.max(bottom, neighbourTop + 1); blockY <= top; blockY++) {
          writer.push(blockX, blockY, blockZ, side.face, colourAt(blockY));
        }
      }
    }
  }

  return {
    faces: writer.values.slice(0, writer.count * 4),
    faceCount: writer.count,
    colours: new Uint8Array(colourValues),
    models,
    ...size,
  };
}
